import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { assets } from '../../../assets/assets';

/** Secura Web : sélection des employés du département pour un projet (même rendu que SelectEmployeesModal des shifts) */
const SelectProjectEmployeesModal = ({
  isOpen,
  onClose,
  employees = [],
  selectedEmployees = [],
  onSelectEmployees,
  loading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelected, setLocalSelected] = useState(selectedEmployees);

  useEffect(() => {
    if (isOpen) {
      setLocalSelected(selectedEmployees);
      setSearchTerm('');
    }
  }, [isOpen, selectedEmployees]);

  const filteredEmployees = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return employees.filter(emp => (
      emp.nom_complet?.toLowerCase().includes(searchLower) ||
      emp.email?.toLowerCase().includes(searchLower) ||
      emp.poste?.toLowerCase().includes(searchLower)
    ));
  }, [employees, searchTerm]);

  const allFilteredSelected = filteredEmployees.length > 0 &&
    filteredEmployees.every(emp => localSelected.includes(emp.id || emp.employee_id));

  const handleToggleEmployee = (employeeId) => {
    setLocalSelected(prev => (
      prev.includes(employeeId) ? prev.filter(id => id !== employeeId) : [...prev, employeeId]
    ));
  };

  const handleToggleAll = () => {
    const ids = filteredEmployees.map(emp => emp.id || emp.employee_id);
    setLocalSelected(prev => (
      allFilteredSelected
        ? prev.filter(id => !ids.includes(id))
        : Array.from(new Set([...prev, ...ids]))
    ));
  };

  const handleApply = () => {
    onSelectEmployees(localSelected);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-[10001] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="bg-white rounded-2xl shadow-2xl p-4 w-full max-w-sm max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base font-bold text-gray-900">Sélectionner les employés</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        {/* Recherche */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Rechercher un employé..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Liste des employés */}
        <div
          className="space-y-3 overflow-y-auto pr-1 flex-1"
          style={{ maxHeight: 'calc(100vh - 380px)', minHeight: '200px' }}
        >
          {loading ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">Chargement...</p>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">Aucun employé trouvé</p>
            </div>
          ) : (
            filteredEmployees.map((employee) => {
              const employeeId = employee.id || employee.employee_id;
              const isSelected = localSelected.includes(employeeId);
              const profileImageUrl = employee.profileImageUrl || employee.imageProfil || employee.image_profil || employee.photo || employee.avatar;

              return (
                <div key={employeeId} className="flex items-center gap-3 w-full">
                  <div className="flex-shrink-0">
                    <img
                      src={profileImageUrl || assets.employes}
                      alt={employee.nom_complet || 'Employé'}
                      className={profileImageUrl ? 'w-12 h-12 rounded-full object-cover border-2' : 'w-12 h-12 object-contain'}
                      style={profileImageUrl ? { borderColor: '#0026E2' } : undefined}
                      onError={(e) => { e.target.src = assets.employes; }}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{employee.nom_complet || 'N/A'}</p>
                    <p className="text-xs text-gray-400 truncate">{employee.poste || 'N/A'}</p>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleToggleEmployee(employeeId)}
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 flex-shrink-0 ${
                      isSelected ? 'bg-[#0026E2]' : 'bg-gray-300'
                    }`}
                  >
                    <span className="sr-only">Sélectionner</span>
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                        isSelected ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="mt-5 pt-4 border-t border-gray-200">
          {localSelected.length > 0 && (
            <div className="mb-3 text-center">
              <p className="text-xs text-gray-600">
                <span className="font-semibold text-gray-900">{localSelected.length}</span> employé{localSelected.length > 1 ? 's' : ''} sélectionné{localSelected.length > 1 ? 's' : ''}
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleToggleAll}
              disabled={filteredEmployees.length === 0}
              className="px-5 py-2 border border-gray-300 rounded-2xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {allFilteredSelected ? 'Tout retirer' : 'Tout sélectionner'}
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="px-5 py-2 bg-[#0026E2] text-white rounded-2xl font-semibold hover:bg-[#0018B8] transition-colors sm:ml-auto"
            >
              Appliquer ({localSelected.length})
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SelectProjectEmployeesModal;
